// src/utility/pusherManager.js
// ─────────────────────────────────────────────────────────────
// Universal Pusher manager — use this across every feature that
// needs real-time events. Import pusherManager anywhere you need
// to subscribe/unsubscribe without managing Pusher instances.
// ─────────────────────────────────────────────────────────────

import Pusher from 'pusher-js';

// ── Singleton Pusher client ───────────────────────────────────
let _pusherClient = null;

function getClient() {
  if (!_pusherClient) {
    _pusherClient = new Pusher(process.env.REACT_APP_PUSHER_KEY || '56fd8b1ba5df4b243794', {
      cluster: process.env.REACT_APP_PUSHER_CLUSTER || 'ap2',
      encrypted: true,
    });

    _pusherClient.connection.bind('error', (err) => {
      console.error('[Pusher] Connection error:', err);
    });
  }
  return _pusherClient;
}

// ── Active subscriptions registry ────────────────────────────
// Map<channelName, { channel, handlers: Map<event, fn[]> }>
const _subscriptions = new Map();


function subscribe(channelName, eventName, callback) {
  const client = getClient();

  if (!_subscriptions.has(channelName)) {
    const channel = client.subscribe(channelName);
    _subscriptions.set(channelName, { channel, handlers: new Map() });
  }

  const sub = _subscriptions.get(channelName);

  if (!sub.handlers.has(eventName)) {
    sub.handlers.set(eventName, []);
    // Bind once per event on the Pusher channel; fan-out to our callbacks
    sub.channel.bind(eventName, (data) => {
      const cbs = sub.handlers.get(eventName) || [];
      cbs.forEach(fn => fn(data));
    });
  }

  sub.handlers.get(eventName).push(callback);

  // Return cleanup
  return () => unsubscribeCallback(channelName, eventName, callback);
}

// ─────────────────────────────────────────────────────────────
// unsubscribeCallback — remove a single callback
// ─────────────────────────────────────────────────────────────
function unsubscribeCallback(channelName, eventName, callback) {
  const sub = _subscriptions.get(channelName);
  if (!sub) return;

  const cbs = sub.handlers.get(eventName) || [];
  sub.handlers.set(eventName, cbs.filter(fn => fn !== callback));

  // If no handlers remain for this channel, unsubscribe from Pusher
  const totalHandlers = [...sub.handlers.values()].reduce((n, arr) => n + arr.length, 0);
  if (totalHandlers === 0) {
    sub.channel.unbind_all();
    getClient().unsubscribe(channelName);
    _subscriptions.delete(channelName);
  }
}

// ─────────────────────────────────────────────────────────────
// unsubscribeChannel — remove ALL listeners on a channel
// ─────────────────────────────────────────────────────────────
function unsubscribeChannel(channelName) {
  const sub = _subscriptions.get(channelName);
  if (!sub) return;
  sub.channel.unbind_all();
  getClient().unsubscribe(channelName);
  _subscriptions.delete(channelName);
}

// ─────────────────────────────────────────────────────────────
// subscribeAnalysisPipeline — convenience wrapper specifically
// for document forgery analysis progress events.
//
// onStep(stepData) is called for every "analysis-progress" event.
// Returns cleanup fn.
//
// stepData shape:
// {
//   socket_id, doc_id, detail_url,
//   step, step_label, step_index, total_steps,
//   status,   // "in_progress" | "completed" | "failed"
//   message,
//   error,
//   issues_count, overall_risk_score  // on analysis_complete
// }
// ─────────────────────────────────────────────────────────────
function subscribeAnalysisPipeline(socketId, { onStep, onComplete, onFailed } = {}) {
  // Strip private- prefix if the backend sends it — public channels don't
  // need auth and won't trigger a /pusher/auth 404.
  // Strip private- prefix — public channels don't trigger /pusher/auth
  const channelName = socketId.replace(/^private-/, '');
  const unsub = subscribe(channelName, 'analysis-progress', (data) => {
    onStep?.(data);

    if (data.status === 'completed') {
      onComplete?.(data);
      setTimeout(() => unsubscribeChannel(channelName), 500);
    }
    if (data.status === 'failed') {
      onFailed?.(data);
      setTimeout(() => unsubscribeChannel(channelName), 500);
    }
  });

  return unsub;
}

// ─────────────────────────────────────────────────────────────
// isConnected — quick health check
// ─────────────────────────────────────────────────────────────
function isConnected() {
  return getClient().connection.state === 'connected';
}

const pusherManager = {
  subscribe,
  unsubscribeCallback,
  unsubscribeChannel,
  subscribeAnalysisPipeline,
  isConnected,
  getClient,
};

export default pusherManager;